import { Box } from "@mui/material";
import { alpha, lighten } from "@mui/material/styles";
import { blue, green, grey, red } from "@mui/material/colors";
import ErrorIcon from "@mui/icons-material/Error";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import type { FC } from "react";
import type { MalnutritionStatus } from "../../types/Malnutrition";

interface StatusLook {
  label: string;
  color: string;
  background: string;
  icon: "error" | "check";
}

const AT_RISK: StatusLook = {
  label: "At-risk",
  color: red[800],
  // MUI's error-alert tint, #FDEDED.
  background: lighten(red[400], 0.9),
  icon: "error",
};

// How a status reads in the list: high and medium are both "At-risk" here,
// while the card header keeps the split.
export const LIST_STATUS: Record<MalnutritionStatus, StatusLook> = {
  high_risk: AT_RISK,
  medium_risk: AT_RISK,
  protocol: {
    label: "Protocol",
    color: blue[900],
    background: blue[50],
    icon: "error",
  },
  following: {
    label: "Following",
    color: grey[700],
    background: grey[50],
    icon: "check",
  },
  consult: {
    label: "Consult",
    color: green[900],
    background: green[50],
    icon: "check",
  },
};

// The list asks for flagged patients only, and an active assessment can carry
// no status (or one the FE can't map), so those still read as flagged.
export const statusLabel = (status: MalnutritionStatus | null): string =>
  (status && LIST_STATUS[status]?.label) || "Flagged";

const sxStyles = {
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.25rem",
    height: "1.5625rem",
    boxSizing: "border-box",
    padding: "0 0.625rem 0 0.25rem",
    borderRadius: "999px",
    border: "1px solid",
    fontSize: "0.875rem",
    whiteSpace: "nowrap",
  },
  icon: { fontSize: "1.125rem" },
  noStatus: { fontSize: "0.875rem", color: grey[600] },
};

const MalnutritionStatusChip: FC<{ status: MalnutritionStatus | null }> = ({
  status,
}) => {
  const look = status ? LIST_STATUS[status] : null;
  if (!look) {
    return <Box sx={sxStyles.noStatus}>{statusLabel(status)}</Box>;
  }

  const Icon = look.icon === "check" ? CheckCircleIcon : ErrorIcon;
  return (
    <Box
      aria-label={`Malnutrition status: ${look.label}`}
      sx={{
        ...sxStyles.chip,
        color: look.color,
        background: look.background,
        borderColor: alpha(look.color, 0.4),
      }}
    >
      <Icon sx={sxStyles.icon} />
      {look.label}
    </Box>
  );
};

export default MalnutritionStatusChip;
